import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import { Calendar, Send } from "lucide-react";
import { toast } from "sonner";

const INDUSTRIES = ["Power & Energy", "Data Centers", "Defense", "Mining", "Oil & Gas", "Manufacturing", "Other"];
const PRODUCTS = ["INSTACLEAN 100", "INSTACLEAN 200", "INSTACLEAN 50", "FAPS (Fire & Arc Protection)", "IoT / AI Solutions", "Not sure yet"];

export default function DemoRequestPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    role: "", industry: "", product_interest: "", preferred_date: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/demo-requests", form);
      setOk(true);
      toast.success(t("demo.success"));
      setForm({ name: "", email: "", company: "", phone: "", role: "", industry: "", product_interest: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="demo-page" className="pb-24">
      <section className="border-b border-slate-800 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950 to-amber-950/10" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader kicker="BOOK A DEMO" title={t("demo.title")} description={t("demo.subtitle")} testIdPrefix="demo" />
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <form onSubmit={submit} className="border border-slate-800 p-8 bg-slate-950" data-testid="demo-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t("contact.name")} testid="demo-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Field label={t("contact.email")} testid="demo-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Field label={t("contact.company")} testid="demo-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} required />
              <Field label={t("contact.phone")} testid="demo-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label={t("demo.role")} testid="demo-role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
              <Select label={t("demo.industry")} testid="demo-industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} options={INDUSTRIES} />
              <Select label={t("demo.product")} testid="demo-product" value={form.product_interest} onChange={(v) => setForm({ ...form, product_interest: v })} options={PRODUCTS} />
              <Field label={t("demo.preferred_date")} testid="demo-date" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} />
            </div>
            <div className="mt-5">
              <label className="block">
                <span className="text-xs font-mono-spec text-slate-400">{t("contact.message")}</span>
                <textarea
                  data-testid="demo-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
                />
              </label>
            </div>
            <button
              type="submit"
              data-testid="demo-submit"
              disabled={submitting}
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-500 text-slate-950 font-bold px-6 py-3 hover:bg-amber-400 transition-colors rounded-sm disabled:opacity-60"
            >
              {submitting ? "Submitting…" : t("demo.submit")} <Send size={14} />
            </button>
            {ok && <div className="mt-4 text-sm text-amber-400 font-mono-spec" data-testid="demo-success">{t("demo.success")}</div>}
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono-spec text-slate-400">{label}{required && <span className="text-amber-400"> *</span>}</span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono-spec text-slate-400">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
      >
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
